import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

// Sub-components matching Figma design
import IntegrationCatalog from "./components/IntegrationCatalog";
import ConnectedPartnerDashboard from "./components/ConnectedPartnerDashboard";
import DeliveryLogDrawer from "./components/DeliveryLogDrawer";

// Hooks & Types
import {
  useDeliveryPartners,
  useDeliveryStats,
  useStorePreference,
  useIntegratedOrderList,
  useSyncOrders,
  useRetryOrderSync,
  useSettlementSummary,
  useSettlementLedger,
} from "./hooks";
import { DeliveryPartner, IntegratedOrder } from "./types";

const INITIAL_PARTNERS: DeliveryPartner[] = [
  {
    id: "evex",
    integration_key: "EVEX_DELIVERY",
    name: "EVEX Delivery",
    logo: "",
    status: "Available",
    connection_status: "not_connected",
    subtitle: "Nationwide (Nepal)",
    coverage: "Nationwide (Nepal)",
    locationType: "Nationwide",
    tags: ["domestic", "same-day", "cod"],
    estTime: "1-2 Days",
    startingFrom: "Rs. 60",
    starting_price: "Rs. 60",
    merchantId: "evx_a1b2c3d4e5f6",
    networkDetails: "Automated Last-Mile Logistics • Nepal Express Network",
  },
  {
    id: "ncm",
    integration_key: "NCM_DELIVERY",
    name: "Nepal Can Move",
    logo: "",
    status: "Connected",
    connection_status: "connected",
    subtitle: "Nationwide",
    coverage: "Nationwide",
    locationType: "Nationwide",
    tags: ["COD Support", "Standard"],
    estTime: "2-3 Days",
    startingFrom: "NPR 200",
    starting_price: "NPR 200",
    merchantId: "NCM-NP-88321",
    networkDetails: "Nationwide Doorstep Delivery • 77 Districts Coverage",
  },
  {
    id: "aramex",
    integration_key: "ARAMEX_NEPAL",
    name: "Aramex Nepal",
    logo: "",
    status: "Available",
    connection_status: "not_connected",
    subtitle: "International",
    coverage: "International",
    locationType: "International",
    tags: ["Express", "Global"],
    estTime: "5-7 Days",
    startingFrom: "Contact",
    starting_price: "Contact",
    merchantId: "ARMX-GLB-501",
    networkDetails: "Cross-Border Worldwide Cargo & Document Dispatch",
  },
  {
    id: "pathao",
    integration_key: "PATHAO_DELIVERY",
    name: "Pathao Delivery",
    logo: "",
    status: "Connected",
    connection_status: "connected",
    subtitle: "Kathmandu Valley",
    coverage: "Kathmandu Valley",
    locationType: "Local",
    tags: ["Automated Last-Mile", "Express Network"],
    estTime: "1-2 Days",
    startingFrom: "NPR 120",
    starting_price: "NPR 120",
    merchantId: "PTH-KTM-99214",
    networkDetails: "Automated Last-Mile Logistics • Kathmandu Valley Express Network",
  },
];

const IntegrationPage: React.FC = () => {
  const { t } = useTranslation();

  // Order Pagination & Filter States
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<string>("");

  // Ledger Pagination & Filter States
  const [ledgerPage, setLedgerPage] = useState<number>(1);
  const [ledgerEventType, setLedgerEventType] = useState<string>("");

  // Custom Hooks for Backend API Integration
  const { partnersList, fetchPartners } = useDeliveryPartners();
  const { stats, fetchStats } = useDeliveryStats();
  const { preference, fetchPreference, updatePreference } = useStorePreference();
  const {
    orders,
    loading,
    totalCount,
    totalPages,
    pageSize,
    fetchIntegratedOrders,
  } = useIntegratedOrderList();
  const { syncing, syncOrders } = useSyncOrders();
  const { retrying, retrySync } = useRetryOrderSync();
  const { settlementSummary, fetchSettlementSummary } = useSettlementSummary();
  const {
    transactions,
    loadingLedger,
    totalCount: ledgerTotalCount,
    totalPages: ledgerTotalPages,
    fetchSettlementLedger,
  } = useSettlementLedger();

  // Local state for partners catalog & selected connected partner
  const [partners, setPartners] = useState<DeliveryPartner[]>(INITIAL_PARTNERS);
  const [activePartner, setActivePartner] = useState<DeliveryPartner | null>(null);

  // Delivery log drawer state
  const [selectedOrderForLogs, setSelectedOrderForLogs] = useState<IntegratedOrder | null>(null);
  const [showLogDrawer, setShowLogDrawer] = useState(false);

  // Fetch partners catalog, store preference, stats, settlement summary on mount
  useEffect(() => {
    fetchPartners();
    fetchPreference();
    fetchStats(dateRange);
    fetchSettlementSummary();
  }, [fetchPartners, fetchPreference, fetchStats, fetchSettlementSummary, dateRange]);

  // Initial order fetch without forced pre-applied filter strings
  useEffect(() => {
    fetchIntegratedOrders(currentPage, 20, statusFilter, dateRange);
  }, [currentPage, statusFilter, dateRange, fetchIntegratedOrders]);

  // Initial ledger fetch
  useEffect(() => {
    fetchSettlementLedger(ledgerPage, 20, ledgerEventType);
  }, [ledgerPage, ledgerEventType, fetchSettlementLedger]);

  // Sync partnersList from API if available
  useEffect(() => {
    if (partnersList && partnersList.length > 0) {
      setPartners(partnersList);
    }
  }, [partnersList]);

  // Sync preference state with EVEX partner in catalog
  useEffect(() => {
    if (preference) {
      setPartners((prev) =>
        prev.map((p) => {
          if (p.integration_key === "EVEX_DELIVERY" || p.id === "evex") {
            const connStatus = preference.is_enabled
              ? preference.kyc_status === "APPROVED"
                ? "connected"
                : "pending_kyc"
              : "not_connected";
            return {
              ...p,
              is_connected: preference.is_enabled,
              connection_status: connStatus,
              status: preference.is_enabled ? "Connected" : "Available",
              merchantId: preference.merchant_id || p.merchantId,
              kyc_status: preference.kyc_status,
            };
          }
          return p;
        })
      );
    }
  }, [preference]);

  // Handle connecting a partner
  const handleConnectPartner = useCallback(
    async (partner: DeliveryPartner) => {
      try {
        if (partner.integration_key === "EVEX_DELIVERY" || partner.id === "evex") {
          await updatePreference(true);
        }
        setPartners((prev) =>
          prev.map((p) =>
            p.id === partner.id
              ? { ...p, status: "Connected", connection_status: "connected", is_connected: true }
              : p
          )
        );
        setActivePartner({ ...partner, status: "Connected", connection_status: "connected", is_connected: true });
        toast.success(`${partner.name} delivery integration enabled for your store!`);
      } catch (error) {
        toast.error(`Failed to connect ${partner.name}.`);
      }
    },
    [updatePreference]
  );

  // Handle managing a connected partner
  const handleManagePartner = useCallback((partner: DeliveryPartner) => {
    setActivePartner(partner);
  }, []);

  // Handle disconnecting a partner
  const handleDisconnectPartner = useCallback(
    async (partner: DeliveryPartner) => {
      try {
        if (partner.integration_key === "EVEX_DELIVERY" || partner.id === "evex") {
          await updatePreference(false);
        }
        setPartners((prev) =>
          prev.map((p) =>
            p.id === partner.id
              ? { ...p, status: "Available", connection_status: "not_connected", is_connected: false }
              : p
          )
        );
        setActivePartner(null);
        toast.info(`${partner.name} integration disabled.`);
      } catch (error) {
        toast.error(`Failed to disconnect ${partner.name}.`);
      }
    },
    [updatePreference]
  );

  // Handle manual API Sync (POST /api/v2/delivery/store-preference/sync/)
  const handleSyncNow = useCallback(async () => {
    try {
      const res = await syncOrders();
      toast.success(res?.message || "Vendor profile sync queued. EVEX will be updated shortly.");
      fetchIntegratedOrders(currentPage, 20, statusFilter, dateRange);
      fetchStats(dateRange);
      fetchSettlementSummary();
      fetchSettlementLedger(ledgerPage, 20, ledgerEventType);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to queue vendor profile sync.");
    }
  }, [syncOrders, fetchIntegratedOrders, fetchStats, fetchSettlementSummary, fetchSettlementLedger, currentPage, statusFilter, dateRange, ledgerPage, ledgerEventType]);

  // Handle Order Sync Retry (POST /api/v2/delivery/orders/{id}/retry/)
  const handleRetryOrderSync = useCallback(
    async (orderId: number) => {
      try {
        const res = await retrySync(orderId);
        toast.success(res?.message || `Order sync retry queued for order #${orderId}.`);
        fetchIntegratedOrders(currentPage, 20, statusFilter, dateRange);
        fetchStats(dateRange);
      } catch (error: any) {
        toast.error(error?.response?.data?.message || `Failed to retry sync for order #${orderId}.`);
      }
    },
    [retrySync, fetchIntegratedOrders, fetchStats, currentPage, statusFilter, dateRange]
  );

  // Open delivery logs drawer
  const handleOpenLogs = useCallback((order: IntegratedOrder) => {
    setSelectedOrderForLogs(order);
    setShowLogDrawer(true);
  }, []);

  return (
    <div className="w-100">
      {activePartner ? (
        <ConnectedPartnerDashboard
          partner={activePartner}
          orders={orders}
          stats={stats}
          syncing={syncing}
          onSync={handleSyncNow}
          onDisconnect={() => handleDisconnectPartner(activePartner)}
          onBackToCatalog={() => setActivePartner(null)}
          onOpenLogs={handleOpenLogs}
          onRetryOrderSync={handleRetryOrderSync}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={(p) => setCurrentPage(p)}
          onSearchChange={(q) => {
            setSearchQuery(q);
            setCurrentPage(1);
          }}
          onStatusFilterChange={(s) => {
            setStatusFilter(s);
            setCurrentPage(1);
          }}
          onDateRangeChange={(r) => {
            setDateRange(r);
            setCurrentPage(1);
          }}
          kycStatus={preference?.kyc_status}
          merchantId={preference?.merchant_id}
          settlementSummary={settlementSummary}
          settlementTransactions={transactions}
          loadingLedger={loadingLedger}
          ledgerPage={ledgerPage}
          ledgerTotalPages={ledgerTotalPages}
          ledgerTotalCount={ledgerTotalCount}
          onLedgerPageChange={(p) => setLedgerPage(p)}
          onLedgerEventTypeChange={(t) => {
            setLedgerEventType(t);
            setLedgerPage(1);
          }}
        />
      ) : (
        <IntegrationCatalog
          partners={partners}
          onConnectPartner={handleConnectPartner}
          onManagePartner={handleManagePartner}
        />
      )}

      {/* Delivery Log Drawer */}
      <DeliveryLogDrawer
        show={showLogDrawer}
        order={selectedOrderForLogs}
        onHide={() => setShowLogDrawer(false)}
      />
    </div>
  );
};

export default React.memo(IntegrationPage);
